import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Check, X, Ban, Search, ShieldCheck, Paperclip, ArrowLeft, Lock as LockIcon, CreditCard, ArrowRight, CheckCircle2, Users, MoreVertical, UserX, Flag } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API, authHeaders, fmtErr, fmtDate, fmtTime, mediaUrl } from '../../lib/auth';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';

const ActionKebab = ({ onUnmatch, onBlock, onReport, testidPrefix }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      <button type="button" className="conv-kebab" onClick={() => setOpen((o) => !o)} data-testid={`${testidPrefix}-kebab-btn`}><MoreVertical size={16} /></button>
      {open && (
        <div className="kebab-menu" data-testid={`${testidPrefix}-kebab-menu`}>
          <button type="button" onClick={() => { setOpen(false); onUnmatch(); }} data-testid={`${testidPrefix}-unmatch-btn`}><UserX size={14} /> Unmatch</button>
          <button type="button" onClick={() => { setOpen(false); onBlock(); }} data-testid={`${testidPrefix}-block-btn`}><Ban size={14} /> Block</button>
          <button type="button" onClick={() => { setOpen(false); onReport(); }} data-testid={`${testidPrefix}-report-btn`}><Flag size={14} /> Block &amp; Report Spam</button>
        </div>
      )}
    </div>
  );
};

const Row = ({ c, children }) => (
  <div className="dash-sub" data-testid={`connection-${c.id}`}>
    <div>
      <b>{c.other?.first_name || '—'}</b>{' '}
      <span style={{ fontFamily: 'IBM Plex Mono', fontSize: 11, color: 'var(--muted)' }}>{c.direction === 'sent' ? c.to_krysha_id : c.from_krysha_id}</span>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
        {[c.other?.age && `${c.other.age} yrs`, c.other?.city].filter(Boolean).join(' · ')}
        {c.other?.age || c.other?.city ? ' · ' : ''}{fmtDate(c.created_at)}
      </div>
    </div>
    {children}
  </div>
);

const ChatGate = ({ status, otherName, onPay }) => {
  const [showFull, setShowFull] = useState(true);
  if (!status || !status.blocked) return null;
  if (status.phase === 'expired') {
    return (
      <div className="chat-gate chat-gate-expired" data-testid="chat-gate-expired">
        <LockIcon size={15} /> Your free chat with {otherName} has ended. Renewal will be available here soon.
      </div>
    );
  }
  if (status.phase === 'first_reply' && !showFull) {
    return (
      <button className="chat-gate-mini" onClick={() => setShowFull(true)} data-testid="chat-gate-reopen">
        Reply for ₹{status.price} to continue chatting →
      </button>
    );
  }
  return (
    <div className="chat-gate" data-testid={`chat-gate-${status.phase}`}>
      <LockIcon size={16} className="chat-gate-icon" />
      <div className="chat-gate-copy">
        <b>{status.phase === 'first_message' ? 'Start the Conversation' : `Reply to ${otherName}`}</b>
        <p>
          {status.phase === 'first_message'
            ? `Sending your first message to ${otherName} is ₹${status.price}. Once they reply, this chat is free for 30 days.`
            : `${otherName} sent you a message. Your first reply is ₹${status.price} — after that this chat is free for 30 days.`}
        </p>
      </div>
      <div className="chat-gate-actions">
        <button className="btn btn-red btn-sm" onClick={onPay} data-testid="chat-pay-btn">
          {status.phase === 'first_message' ? `Start Conversation — ₹${status.price}` : `Reply for ₹${status.price}`}
        </button>
        {status.phase === 'first_reply' && (
          <button className="btn btn-ghost btn-sm" onClick={() => setShowFull(false)} data-testid="chat-gate-later-btn">Maybe Later</button>
        )}
      </div>
    </div>
  );
};

export const InboxTab = ({ me, flash }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('chats');
  const [connections, setConnections] = useState([]);
  const [convs, setConvs] = useState([]);
  const [convSearch, setConvSearch] = useState('');
  const [activeConv, setActiveConv] = useState(null);
  const [convStatus, setConvStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [searchId, setSearchId] = useState('');
  const [found, setFound] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [sendingImg, setSendingImg] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paidJustNow, setPaidJustNow] = useState(false);
  const [params, setParams] = useSearchParams();
  const imgInputRef = useRef(null);
  const msgEnd = useRef(null);

  const loadConnections = useCallback(() => axios.get(`${API}/connections`, { headers: authHeaders() }).then(({ data }) => setConnections(data)).catch(() => {}), []);
  const loadConvs = useCallback(() => axios.get(`${API}/conversations`, { headers: authHeaders() }).then(({ data }) => setConvs(data)).catch(() => {}), []);

  useEffect(() => {
    loadConnections(); loadConvs();
    const t = setInterval(() => { loadConnections(); loadConvs(); }, 12000);
    return () => clearInterval(t);
  }, [loadConnections, loadConvs]);

  const loadMessages = useCallback((convId) => {
    axios.get(`${API}/conversations/${convId}/messages`, { headers: authHeaders() })
      .then(({ data }) => setMessages(data)).catch(() => {});
  }, []);

  const loadStatus = useCallback((convId) => {
    axios.get(`${API}/conversations/${convId}`, { headers: authHeaders() })
      .then(({ data }) => setConvStatus(data.status)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeConv) return;
    loadMessages(activeConv.id);
    loadStatus(activeConv.id);
    const t = setInterval(() => { loadMessages(activeConv.id); loadStatus(activeConv.id); }, 4000);
    return () => clearInterval(t);
  }, [activeConv, loadMessages, loadStatus]);

  useEffect(() => { msgEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const openConv = (c) => { setActiveConv(c); setPaidJustNow(false); };
  const closeConv = () => { setActiveConv(null); setMessages([]); setConvStatus(null); loadConvs(); };
  const openProfile = (p) => navigate(`/member/${p.krysha_id}`, { state: { profile: p } });

  useEffect(() => {
    const convId = params.get('conv');
    if (convId && !activeConv && convs.length > 0) {
      const match = convs.find((c) => c.id === convId);
      if (match) {
        openConv(match);
        setParams({}, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convs, params]);

  const respond = async (id, action) => {
    try {
      const { data } = await axios.post(`${API}/connections/${id}/respond`, { action }, { headers: authHeaders() });
      flash(data.conversation_id ? 'Connection accepted — you can start chatting from Inbox.' : 'Request declined.');
      loadConnections(); loadConvs();
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const blockUser = async (kid) => {
    try {
      const { data } = await axios.post(`${API}/users/block`, { krysha_id: kid }, { headers: authHeaders() });
      flash(data.message); loadConnections();
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const unmatchConv = async (connectionId, isActive) => {
    if (!connectionId) return;
    if (!window.confirm('Unmatch this connection? It will be removed from your Inbox.')) return;
    try {
      const { data } = await axios.post(`${API}/connections/${connectionId}/unmatch`, {}, { headers: authHeaders() });
      flash(data.message);
      if (isActive) closeConv(); else { loadConvs(); loadConnections(); }
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const blockConv = async (krysha_id, isActive) => {
    if (!window.confirm(`Block ${krysha_id}? They won't be able to contact you again.`)) return;
    try {
      const { data } = await axios.post(`${API}/users/block`, { krysha_id }, { headers: authHeaders() });
      flash(data.message);
      if (isActive) closeConv(); else { loadConvs(); loadConnections(); }
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const reportConv = async (krysha_id, isActive) => {
    if (!window.confirm(`Block and report ${krysha_id} as spam?`)) return;
    try {
      const { data } = await axios.post(`${API}/users/report`, { krysha_id }, { headers: authHeaders() });
      flash(data.message);
      if (isActive) closeConv(); else { loadConvs(); loadConnections(); }
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    const text = msgText.trim();
    if (!text || convStatus?.blocked) return;
    setMsgText('');
    try {
      await axios.post(`${API}/conversations/${activeConv.id}/messages`, { text }, { headers: authHeaders() });
      loadMessages(activeConv.id); loadStatus(activeConv.id);
    } catch (err) {
      if (err.response?.status === 402) { loadStatus(activeConv.id); flash('Payment required to send this message.'); }
      else flash(fmtErr(err.response?.data?.detail));
    }
  };

  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const pickImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (convStatus?.blocked) { flash('Complete the payment above before sending photos.'); return; }
    if (!ALLOWED_TYPES.includes(file.type)) { flash('Please choose a JPG, PNG, WEBP or GIF image'); return; }
    if (file.size > 5 * 1024 * 1024) { flash('Image must be smaller than 5 MB'); return; }
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  const cancelImage = () => { setImgFile(null); setImgPreview(null); };

  const sendImage = async () => {
    if (!imgFile || sendingImg) return;
    setSendingImg(true);
    const fd = new FormData();
    fd.append('file', imgFile);
    try {
      await axios.post(`${API}/conversations/${activeConv.id}/messages/image`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      cancelImage();
      loadMessages(activeConv.id); loadStatus(activeConv.id);
    } catch (err) {
      if (err.response?.status === 402) { loadStatus(activeConv.id); flash('Payment required to send this image.'); }
      else flash(fmtErr(err.response?.data?.detail));
    }
    setSendingImg(false);
  };

  const payAndUnlock = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/conversations/${activeConv.id}/pay`, { type: convStatus.phase }, { headers: authHeaders() });
      setConvStatus(data.status);
      setPaidJustNow(true);
      flash('Payment successful. You can send your message now.');
      setTimeout(() => setPayOpen(false), 900);
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
    setPaying(false);
  };

  const doSearch = async (e) => {
    e.preventDefault();
    setFound(null);
    try {
      const { data } = await axios.get(`${API}/users/search/${encodeURIComponent(searchId.trim())}`, { headers: authHeaders() });
      setFound(data);
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const sendToFound = async () => {
    try {
      await axios.post(`${API}/connections/request`, { to_krysha_id: found.krysha_id }, { headers: authHeaders() });
      flash(`Connect request sent to ${found.krysha_id}`);
      setFound(null); setSearchId(''); loadConnections();
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const received = connections.filter((c) => c.direction === 'received' && c.status === 'pending');
  const sent = connections.filter((c) => c.direction === 'sent' && c.status === 'pending');
  const accepted = connections.filter((c) => c.status === 'accepted');
  const filteredConvs = convs.filter((c) => {
    const q = convSearch.trim().toLowerCase();
    if (!q) return true;
    return c.other?.first_name?.toLowerCase().includes(q) || c.other?.krysha_id?.toLowerCase().includes(q);
  });

  const convPreview = (c) => {
    if (c.status?.blocked && c.status.phase === 'first_message') return 'Payment needed to start chatting';
    if (c.status?.blocked && c.status.phase === 'first_reply') return `${c.other?.first_name} is waiting for your reply`;
    if (c.status?.blocked && c.status.phase === 'expired') return 'Free chat period ended';
    if (!c.last_message) return 'Say hello 👋';
    if (c.last_message.type === 'image') return '📷 Photo';
    return c.last_message.text;
  };

  // ===== Full-screen chat =====
  if (activeConv) {
    const otherName = activeConv.other?.first_name || 'this member';
    return (
      <div className="chat-fullscreen" data-testid="chat-view">
        <div className="chat-head">
          <button className="chat-back" onClick={closeConv} data-testid="chat-back-btn"><ArrowLeft size={19} /></button>
          <button type="button" className="chat-head-identity" onClick={() => openProfile({ ...activeConv.other, conversation_id: activeConv.id })} data-testid="chat-head-profile-btn">
            {activeConv.other?.photo_url ? (
              <img className="chat-head-avatar" src={mediaUrl(activeConv.other.photo_url, true)} alt={otherName} />
            ) : <div className="chat-head-avatar chat-head-avatar-empty">{otherName[0]?.toUpperCase()}</div>}
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <b>{otherName}</b>
              <div style={{ fontFamily: 'IBM Plex Mono', fontSize: 10.5, color: 'var(--muted)' }}>{activeConv.other?.krysha_id}</div>
            </div>
          </button>
          {convStatus?.free_active && <span className="pay-badge" data-testid="chat-free-badge">Free chat</span>}
          <ActionKebab
            testidPrefix="chat-head"
            onUnmatch={() => unmatchConv(activeConv.other?.connection_id, true)}
            onBlock={() => blockConv(activeConv.other?.krysha_id, true)}
            onReport={() => reportConv(activeConv.other?.krysha_id, true)}
          />
        </div>
        <div className="chat-msgs">
          {messages.map((m) => (
            <div key={m.id} className={`bubble ${m.sender_id === me.id ? 'mine' : ''} ${m.type === 'image' ? 'bubble-img' : ''}`} data-testid={`message-${m.id}`}>
              {m.type === 'image' ? (
                <img className="chat-img" src={mediaUrl(m.image_url, true)} alt="Shared" data-testid={`message-image-${m.id}`} />
              ) : m.text}
              <span className="bubble-time">{fmtTime(m.created_at)}{m.sender_id === me.id && m.read_at ? ' · read' : ''}</span>
            </div>
          ))}
          {messages.length === 0 && !convStatus?.blocked && (
            <p style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', marginTop: 30 }}>No messages yet. Say hello to {otherName}!</p>
          )}
          <div ref={msgEnd} />
        </div>

        <ChatGate status={convStatus} otherName={otherName} onPay={() => setPayOpen(true)} />

        {imgPreview && (
          <div className="chat-img-preview" data-testid="chat-image-preview">
            <img src={imgPreview} alt="To send" />
            <div>
              <button type="button" className="btn btn-red btn-sm" onClick={sendImage} disabled={sendingImg} data-testid="chat-image-send-btn">{sendingImg ? 'Sending…' : 'Send Photo'}</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={cancelImage} data-testid="chat-image-cancel-btn"><X size={14} /></button>
            </div>
          </div>
        )}
        <form className="chat-input" onSubmit={sendMsg}>
          <button type="button" className="chat-attach-btn" onClick={() => imgInputRef.current?.click()} title="Attach image" disabled={convStatus?.blocked} data-testid="chat-attach-btn"><Paperclip size={17} /></button>
          <input ref={imgInputRef} type="file" accept="image/*" hidden onChange={pickImage} data-testid="chat-image-input" />
          <input value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder={convStatus?.blocked ? 'Complete payment above to chat…' : 'Message…'} disabled={convStatus?.blocked} data-testid="chat-message-input" />
          <button className="btn btn-red btn-sm chat-send-btn" type="submit" disabled={convStatus?.blocked} data-testid="chat-send-btn"><Send size={14} /></button>
        </form>

        <Dialog open={payOpen} onOpenChange={(o) => setPayOpen(o)}>
          <DialogContent className="max-w-md p-0 overflow-hidden border-0">
            <DialogTitle className="sr-only">Pay to Unlock Chat</DialogTitle>
            {!paidJustNow ? (
              <div style={{ padding: 26 }}>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--pink)', color: 'var(--red)', display: 'grid', placeItems: 'center', margin: '0 auto 10px' }}>
                    <CreditCard size={24} />
                  </div>
                  <h3 style={{ font: '700 22px "Playfair Display"', margin: 0 }} data-testid="pay-modal-title">
                    {convStatus?.phase === 'first_message' ? 'Start Conversation' : 'Unlock Your Reply'}
                  </h3>
                </div>
                <div style={{ border: '1px solid var(--line)', borderRadius: 14, padding: 16, marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                    <span style={{ color: 'var(--muted)' }}>Chatting with</span><b>{otherName}</b>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed var(--line)', paddingTop: 10, marginTop: 4 }}>
                    <span style={{ color: 'var(--muted)', fontSize: 14 }}>Amount Payable</span>
                    <b style={{ font: '700 26px "Playfair Display"', color: 'var(--red)' }} data-testid="pay-modal-amount">₹{convStatus?.price}</b>
                  </div>
                </div>
                <div style={{ background: '#fff5f7', border: '1px solid #f3d3db', borderRadius: 12, padding: '10px 12px', fontSize: 11.5, color: '#83767a', display: 'flex', gap: 8, marginBottom: 16 }}>
                  <LockIcon size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--red)' }} />
                  Secure demo/sandbox payment. Live payment gateway (UPI / Cards / Netbanking) will be connected soon — this unlock only applies to your chat with {otherName}.
                </div>
                <button className="btn btn-red" style={{ width: '100%' }} onClick={payAndUnlock} disabled={paying} data-testid="pay-confirm-btn">
                  {paying ? 'Processing…' : <>Pay ₹{convStatus?.price} <ArrowRight size={17} /></>}
                </button>
              </div>
            ) : (
              <div style={{ padding: 30, textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#eafaf0', color: '#1f9d55', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
                  <CheckCircle2 size={32} />
                </div>
                <h3 style={{ font: '700 22px "Playfair Display"', margin: '0 0 6px' }} data-testid="pay-success">Payment successful!</h3>
                <p style={{ fontSize: 13.5, color: 'var(--muted)' }}>You can send your message to {otherName} now.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ===== Inbox list / requests =====
  return (
    <div style={{ display: 'grid', gap: 16 }} data-testid="inbox-section">
      <div className="dash-tabs" data-testid="inbox-tabs">
        <button className={`dash-tab ${tab === 'chats' ? 'on' : ''}`} onClick={() => setTab('chats')} data-testid="inbox-tab-chats">Chats</button>
        <button className={`dash-tab ${tab === 'requests' ? 'on' : ''}`} onClick={() => setTab('requests')} data-testid="inbox-tab-requests">
          Requests {received.length > 0 && <span className="dash-badge">{received.length}</span>}
        </button>
      </div>

      {tab === 'chats' ? (
        <div className="dash-card dash-inbox">
          <div className="conv-search">
            <Search size={15} />
            <input value={convSearch} onChange={(e) => setConvSearch(e.target.value)} placeholder="Search conversations" data-testid="conv-search-input" />
          </div>
          {filteredConvs.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 13, padding: '6px 2px' }}>No conversations yet. Accept a connect request to start chatting.</p>}
          {filteredConvs.map((c) => (
            <div className="conv-row" key={c.id} role="button" tabIndex={0} onClick={() => openConv(c)} data-testid={`conversation-${c.id}`}>
              <span
                role="button" tabIndex={0} style={{ flexShrink: 0 }}
                onClick={(e) => { e.stopPropagation(); openProfile({ ...c.other, conversation_id: c.id }); }}
                data-testid={`conversation-avatar-${c.id}`}
              >
                {c.other?.photo_url ? (
                  <img className="found-avatar" src={mediaUrl(c.other.photo_url, true)} alt={c.other?.first_name} />
                ) : <div className="found-avatar">{c.other?.first_name?.[0] || '?'}</div>}
              </span>
              <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <b>{c.other?.first_name}</b>
                  <span className="conv-time">{fmtTime((c.last_message || {}).created_at || c.created_at)}</span>
                </div>
                <div className={`conv-last ${c.status?.blocked ? 'conv-last-gated' : ''}`}>{convPreview(c)}</div>
              </div>
              {c.unread > 0 && <span className="dash-badge" data-testid={`unread-${c.id}`}>{c.unread}</span>}
              <ActionKebab
                testidPrefix={`conv-${c.id}`}
                onUnmatch={() => unmatchConv(c.other?.connection_id, false)}
                onBlock={() => blockConv(c.other?.krysha_id, false)}
                onReport={() => reportConv(c.other?.krysha_id, false)}
              />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="dash-card">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Users size={17} style={{ color: 'var(--red)' }} /> Received Requests {received.length > 0 && <span className="dash-badge" data-testid="received-count">{received.length}</span>}</h3>
            {received.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 13 }}>No new requests right now.</p>}
            {received.map((c) => (
              <Row c={c} key={c.id}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-red btn-sm" onClick={() => respond(c.id, 'accept')} data-testid={`accept-${c.id}`}><Check size={13} /> Accept</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => respond(c.id, 'decline')} data-testid={`decline-${c.id}`}><X size={13} /> Decline</button>
                  <button className="btn btn-ghost btn-sm" title="Block" onClick={() => blockUser(c.from_krysha_id)} data-testid={`block-${c.id}`}><Ban size={13} /></button>
                </div>
              </Row>
            ))}
          </div>

          <div className="dash-card">
            <h3>Sent Requests</h3>
            {sent.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 13 }}>You haven't sent any pending requests.</p>}
            {sent.map((c) => <Row c={c} key={c.id}><span className="pay-badge badge-muted">Waiting for reply</span></Row>)}
          </div>

          <div className="dash-card">
            <h3>Accepted Connections</h3>
            {accepted.length === 0 && <p style={{ color: 'var(--muted)', fontSize: 13 }}>No accepted connections yet.</p>}
            {accepted.map((c) => (
              <Row c={c} key={c.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="pay-badge">Connected</span>
                  <ActionKebab
                    testidPrefix={`accepted-${c.id}`}
                    onUnmatch={() => unmatchConv(c.id, false)}
                    onBlock={() => blockConv(c.other?.krysha_id, false)}
                    onReport={() => reportConv(c.other?.krysha_id, false)}
                  />
                </div>
              </Row>
            ))}
          </div>

          <div className="dash-card">
            <h3>Know someone's Krysha ID?</h3>
            <form onSubmit={doSearch} className="dash-search">
              <input value={searchId} onChange={(e) => setSearchId(e.target.value)} placeholder="e.g. DINESH-KRY-100001" data-testid="krysha-search-input" />
              <button className="btn btn-red btn-sm" type="submit" data-testid="krysha-search-btn"><Search size={14} /> Search</button>
            </form>
            {found && (
              <div className="found-card" data-testid="found-profile">
                {found.photo_url ? <img className="found-avatar" src={mediaUrl(found.photo_url)} alt={found.first_name} /> : <div className="found-avatar">{found.first_name?.[0] || '?'}</div>}
                <div style={{ flex: 1 }}>
                  <b>{found.first_name}</b> <span style={{ fontFamily: 'IBM Plex Mono', fontSize: 11, color: 'var(--muted)' }}>{found.krysha_id}</span>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                    {[found.age && `${found.age} yrs`, found.city, found.personality, found.looking].filter(Boolean).join(' · ') || 'Profile details not shared yet'}
                  </div>
                </div>
                <button className="btn btn-red btn-sm" onClick={sendToFound} data-testid="send-request-btn"><Send size={13} /> Send Connect Request</button>
              </div>
            )}
            <p className="dash-privacy"><ShieldCheck size={13} /> Only first name, age, city and interests are shared. Email and mobile numbers stay private.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default InboxTab;
